import type DataFileMap from '@/model/Resource/DataFileMap'
import type MainProject from '@/model/MainProject'
import { TITLE_BACKGROUND_BASE_FOLDER } from '@/features/workspace/settings/settingsConstants'
import { resolveResourceUrlFromFileName } from '@/features/virtual-resource/fileNamePath'
import { getUploadPicSign } from '@/publish/api'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import BrowserUtil from '@/platform/oss/browserUtil'
import type { JsonRecord, RequestTraceCallback } from '@/utils/request/apiRequest'

export type PublishCoverUploadStatus = 'success' | 'skipped' | 'failed'

const UPYUN_API_ORIGIN = 'https://v0.api.upyun.com'

function normalizeUpyunObjectPath(bucket: string, objectName: string): string {
  const normalizedObject = objectName.replace(/^\/+/, '')
  return `/${bucket}/${normalizedObject}`
}

/**
 * 开发环境通过 Vite 上传代理转发又拍云 PUT，避免浏览器跨域。
 * @param bucket 又拍云 bucket。
 * @param objectName 对象路径。
 * @returns 实际请求地址。
 */
function buildUpyunUploadUrl(bucket: string, objectName: string): string {
  const objectPath = normalizeUpyunObjectPath(bucket, objectName)
  if (import.meta.env.DEV) {
    const query = new URLSearchParams({ __upload_origin: UPYUN_API_ORIGIN })
    return `/__dev_proxy_upload${objectPath}?${query.toString()}`
  }
  return `${UPYUN_API_ORIGIN}${objectPath}`
}

async function fetchResourceBytes(resourceUrl: string): Promise<Uint8Array> {
  try {
    const parsed = new URL(resourceUrl, window.location.href)
    if (/\.aliyuncs\.com$/i.test(parsed.hostname) && BrowserUtil.getInstance().isConfigured()) {
      const rootDir = BrowserUtil.getInstance().getRootDir().replace(/^\/+|\/+$/g, '')
      const decodedPath = decodeURIComponent(parsed.pathname).replace(/^\/+/, '')
      const objectKey = rootDir && decodedPath.toLowerCase().startsWith(`${rootDir.toLowerCase()}/`)
        ? decodedPath.slice(rootDir.length + 1)
        : decodedPath
      if (objectKey) {
        return new Uint8Array(await BrowserFileUtils.blobToArrayBuffer(await BrowserUtil.getInstance().getBlob(objectKey)))
      }
    }
  } catch {
  }
  const response = await fetch(resourceUrl)
  if (!response.ok) {
    throw new Error(`封面图片读取失败：HTTP ${response.status}`)
  }
  return await BrowserFileUtils.responseToUint8Array(response)
}

/**
 * 解析又拍云上传签名返回。
 * @param data upload_pic_sign 原始返回。
 * @returns 上传所需字段。
 */
function parseUploadPicSignPayload(data: JsonRecord): {
  bucket: string
  path: string
  sign: string
  date: string
} {
  const payload = (data.data as JsonRecord | undefined) ?? data
  return {
    bucket: String(payload.bucket ?? '').trim(),
    path: String(payload.path ?? '').trim(),
    sign: String(payload.sign ?? '').trim(),
    date: String(payload.date ?? '').trim(),
  }
}

/**
 * 将标题画面封面图上传到作品封面位。
 * @param params 工程、资源根、发布版本与登录态。
 * @returns 上传结果状态与说明。
 */
export async function uploadPublishedCover(params: {
  project: MainProject
  graphicRoot: DataFileMap | null
  resourcePreviewOverrides?: Record<string, string>
  guid: string
  publishVersion: string
  sessionId: string
  onTrace?: RequestTraceCallback
}): Promise<{ status: PublishCoverUploadStatus; detail: string }> {
  const titlePicName = params.project.systemData.Title.TitlePic.name.trim()
  if (!titlePicName) {
    return { status: 'skipped', detail: '未设置封面图片，已跳过' }
  }

  const resourceUrl = resolveResourceUrlFromFileName(
    params.graphicRoot,
    'graphic',
    titlePicName,
    params.resourcePreviewOverrides,
    TITLE_BACKGROUND_BASE_FOLDER,
  )
  if (!resourceUrl) {
    return { status: 'skipped', detail: '封面图片资源不存在，已跳过' }
  }

  let bytes: Uint8Array
  try {
    bytes = await fetchResourceBytes(resourceUrl)
  } catch (error) {
    const message = error instanceof Error ? error.message : '封面图片读取失败'
    return { status: 'failed', detail: message }
  }
  if (!bytes.byteLength) {
    return { status: 'failed', detail: '封面图片为空，无法上传' }
  }

  let signPayload: ReturnType<typeof parseUploadPicSignPayload>
  try {
    const signData = await getUploadPicSign({
      sessionId: params.sessionId,
      guid: params.guid,
      version: params.publishVersion,
      length: bytes.byteLength,
    }, params.onTrace)
    signPayload = parseUploadPicSignPayload(signData)
  } catch (error) {
    const message = error instanceof Error ? error.message : '封面上传签名获取失败'
    return { status: 'failed', detail: message }
  }

  const { bucket, path, sign, date } = signPayload
  if (!bucket || !path || !sign || !date) {
    return { status: 'failed', detail: '封面上传签名返回不完整' }
  }

  const objectName = `${path}${params.guid}_${params.publishVersion}.jpg`
  const uploadUrl = buildUpyunUploadUrl(bucket, objectName)
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      Authorization: sign,
      'x-date': date,
    },
    body: bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
  })
  if (!response.ok) {
    return { status: 'failed', detail: `作品封面上传失败：HTTP ${response.status}` }
  }
  return { status: 'success', detail: '作品封面已上传' }
}
